import { useCallback, useRef } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SpaceSiteSelectionForm {
  siteIds?: string[] | null;
}

export interface UseSpaceSiteSelectionReturn {
  handleSiteSelectionChange: (spaceId: string, siteIds: string[]) => void;
  /**
   * Backfills the map for a space whose site selection was already set as a form default without
   * user interaction (e.g. a create-mode drawer pre-pinned to a single site, whose site selector
   * renders disabled). Unlike `handleSiteSelectionChange`, this does not touch the form's siteIds
   * field or mark it dirty — it only keeps the submit-time map in sync with values the caller
   * already seeded as defaults.
   */
  seedSiteSelection: (spaceId: string, siteIds: string[]) => void;
  spaceSiteSelectionRef: React.MutableRefObject<Map<string, string[]>>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Tracks per-space site selections and merges them into the form's `siteIds` field.
 * Eliminates the need to duplicate the ref+callback pattern in every form-fields component.
 */
export function useSpaceSiteSelection<T extends SpaceSiteSelectionForm>(
  form: UseFormReturn<T>,
): UseSpaceSiteSelectionReturn {
  const spaceSiteSelectionRef = useRef<Map<string, string[]>>(new Map());

  const handleSiteSelectionChange = useCallback(
    (spaceId: string, newSiteIds: string[]): void => {
      spaceSiteSelectionRef.current.set(spaceId, newSiteIds);
      const merged = Array.from(spaceSiteSelectionRef.current.values()).flat();
      // shouldDirty/shouldValidate so edit-mode Save buttons (gated on isDirty/isValid) react to
      // scope changes — a programmatic setValue does not mark the field dirty by default.
      form.setValue('siteIds' as FieldPath<T>, merged as never, { shouldDirty: true, shouldValidate: true });
    },
    [form],
  );

  const seedSiteSelection = useCallback((spaceId: string, siteIds: string[]): void => {
    spaceSiteSelectionRef.current.set(spaceId, siteIds);
  }, []);

  return { handleSiteSelectionChange, seedSiteSelection, spaceSiteSelectionRef };
}
